import React, { useState, useCallback } from "react";
import { MessageSquareOff, AlignLeft } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { FormField } from "../ui/form_field";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../ui/Modal";
import { useToast } from "../ui/toast";
import { router } from "@inertiajs/react";
import { getFormIconClassName } from "@/lib/utils";

interface DismissRecommendationModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceSlug: string;
  recommendationUuid: string;
}

const MAX_REASON_LENGTH = 1000;

export default function DismissRecommendationModal({
  isOpen,
  onClose,
  workspaceSlug,
  recommendationUuid,
}: DismissRecommendationModalProps) {
  const { showToast } = useToast();
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const remainingChars = MAX_REASON_LENGTH - reason.length;

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
      setReason("");
      setErrors({});
    }
  }, [isSubmitting, onClose]);

  const handleReasonChange = (value: string) => {
    setReason(value);
    if (errors.reason) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.reason;
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    const trimmedReason = reason.trim();

    if (trimmedReason.length > MAX_REASON_LENGTH) {
      setErrors({ reason: `Reason must be ${MAX_REASON_LENGTH} characters or less` });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(
        `/workspaces/${workspaceSlug}/recommendations/${recommendationUuid}/dismiss`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
          body: JSON.stringify({
            reason: trimmedReason || undefined,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        showToast("success", result.message || "Recommendation dismissed successfully!");
        handleClose();
        router.reload({ only: ["recommendation_sections", "recommendation_summary"] });
      } else {
        const formattedErrors: Record<string, string> = {};

        if (Array.isArray(result.errors)) {
          result.errors.forEach((error: string) => {
            if (error.toLowerCase().includes("reason")) {
              formattedErrors.reason = error;
            } else {
              showToast("error", error);
            }
          });
        } else {
          Object.entries(result.errors || {}).forEach(([key, value]) => {
            formattedErrors[key] = Array.isArray(value) ? value[0] : value;
          });
        }

        setErrors(formattedErrors);
      }
    } catch {
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} canClose={!isSubmitting} size="md">
      <ModalHeader
        icon={<MessageSquareOff className="w-5 h-5 text-amber-600" />}
        title="Dismiss recommendation"
      />

      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="mb-4 space-y-2 text-sm text-surface-foreground/60">
            <p>Help us improve by sharing why you're dismissing this.</p>
            <p>This will suppress recommendations for this resource for 30 days.</p>
          </div>

          <FormField
            label="Reason (optional)"
            error={errors.reason}
          >
            <Textarea
              value={reason}
              onChange={(e) => handleReasonChange(e.target.value)}
              placeholder="e.g., This instance handles seasonal traffic, This is a temporary resource, Planning to migrate soon..."
              disabled={isSubmitting}
              rows={4}
              maxLength={MAX_REASON_LENGTH}
              aria-invalid={!!errors.reason}
              leftIcon={<AlignLeft className={getFormIconClassName(!!errors.reason)} />}
            />
          </FormField>

          <div className="mt-2 text-right">
            <span
              className={`text-sm ${
                remainingChars < 100
                  ? "text-amber-600 dark:text-amber-500"
                  : "text-surface-foreground/40"
              }`}
            >
              {remainingChars} characters remaining
            </span>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="destructive"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            loadingText="Dismissing..."
          >
            Dismiss
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
